/**
 * Sticky-element offset constants. All values in CSS pixels and
 * mirror the heights baked into the corresponding Tailwind classes.
 *
 * Layout stack (top → bottom):
 *
 *   Topbar          — 74px desktop (>=1001px) / 68px mobile (<=1000px)
 *                         — CR-028/029 (CEW chrome parity, 2026-08-17)
 *                         bumped this from a flat 56px to match
 *                         cew.georgetown.edu's own header heights.
 *   SectionNav      — 60px  (`h-[60px]`)
 *   FilterBar       — 60px  (filter-bar height)
 *
 * Follow-up (2026-08-18, Frank staging review, TD-08a/b, DEC-075): the
 * RESPONSIVE owner for the Topbar height (and everything stacked below
 * it) is now the CSS custom property `--h-topbar` in `globals.css` —
 * `68px` at the base `:root`, overridden to `74px` inside the same
 * `min-width: 1001px` breakpoint the Topbar's own `min-[1001px]:h-…`
 * class flips at. `--offset-nav-stack` and `--offset-filter-stack` are
 * `calc()` expressions built on `var(--h-topbar)`, so they inherit the
 * responsiveness for free with ONE dial (SectionNav, both filter bars,
 * `MetroDetail`'s sticky chart, and the map's `calc(100vh - …)` shell
 * all read the CSS vars directly, not a hardcoded JS-constant px
 * value). A stale JS px value baked into a component would reproduce
 * exactly the BUG-002 gap class this fixed — content peeking out from
 * under the sticky chrome at the breakpoint boundary — so that gap
 * must not exist at any width; there is no accepted slop.
 *
 * The JS constants below (`TOPBAR_HEIGHT`, `TOPBAR_HEIGHT_MOBILE`, and
 * the derived `STICKY_OFFSET_*` sums) are NOT the layout owner anymore.
 * They remain as the source values `constraint-ownership.test.tsx`
 * checks the CSS vars against, and are consumed by two things that
 * can't read a CSS var directly:
 *   - `useIsStuck`'s IntersectionObserver `rootMargin` math (JS, not
 *     CSS) — a ≤6px shadow-timing slop between 600-1000px (where the
 *     desktop `TOPBAR_HEIGHT` constant is used for the observer even
 *     though the CSS var already correctly reads 68px there) is
 *     acceptable and documented, since it only affects WHEN a shadow
 *     transition fires, not element position.
 *   - `MapMobileLayout` (<600px, uses `TOPBAR_HEIGHT_MOBILE` directly
 *     for its own layout math outside the CSS-var-driven desktop path).
 *
 * If any of these heights change, update BOTH the JS constants here
 * AND the CSS var declarations in `globals.css` — the two are asserted
 * equal by `constraint-ownership.test.tsx` so they cannot silently
 * drift.
 *
 * Derived offsets (JS mirror; the CSS vars are the real owner):
 *
 *   STICKY_OFFSET_BELOW_TOPBAR        — 74  (SectionNav pins here)
 *   STICKY_OFFSET_BELOW_SECTION_NAV   — 134 (filter bars pin here)
 *   STICKY_OFFSET_BELOW_FILTER_BAR    — 194 (detail page sticky chart pins
 *                                            below the filter bar; the
 *                                            page adds a small breathing-
 *                                            room buffer)
 */

export const TOPBAR_HEIGHT = 74;
/** CEW's mobile header height (<=1000px) — see doc block above. */
export const TOPBAR_HEIGHT_MOBILE = 68;
export const SECTION_NAV_HEIGHT = 60;
export const FILTER_BAR_HEIGHT = 60;

export const STICKY_OFFSET_BELOW_TOPBAR = TOPBAR_HEIGHT;
export const STICKY_OFFSET_BELOW_SECTION_NAV =
  TOPBAR_HEIGHT + SECTION_NAV_HEIGHT;
export const STICKY_OFFSET_BELOW_FILTER_BAR =
  TOPBAR_HEIGHT + SECTION_NAV_HEIGHT + FILTER_BAR_HEIGHT;

/**
 * TD-08 (DEC-075): additional shared layout constants that were
 * previously magic numbers duplicated across components.
 *
 * These are mirrored as CSS custom properties in `globals.css`
 * (`--h-topbar`, `--offset-nav-stack`, `--offset-filter-stack`,
 * `--modal-inset`) so Tailwind arbitrary classes and JS both draw from
 * declared, test-synced values — `constraint-ownership.test.tsx`
 * asserts the CSS vars equal these exports, so the two sources cannot
 * silently drift.
 */

/** Viewport inset for centered expand modals + the row-peek drawer
 *  (`calc(100dvh - 64px)` heights). Was hardcoded at 3 sites. */
export const MODAL_VIEWPORT_INSET = 64;

/** Metro popup / mobile metro-modal card width. Leaflet's popup takes
 *  this as numeric props (`MetroBubble` min/maxWidth); the Tailwind
 *  `max-w-popup` token carries the same value for the mobile modal. */
export const METRO_POPUP_WIDTH_PX = 560;
